import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    TextField,
    Tooltip,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

import { useTranslation } from 'react-i18next';

import { inventoryAPI, productAPI, itemRecipeAPI } from '../../services/apiService';

import './ItemRecipes.css';

const emptyIngredient = () => ({
    id: null,
    ingredient: '',
    quantity: '',
});

function getProductName(product) {
    return product?.title || product?.name || product?.product_name || `Item #${product?.id ?? ''}`;
}

function ItemRecipes() {
    const { t } = useTranslation();

    const [products, setProducts] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [recipeLines, setRecipeLines] = useState([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [originalLines, setOriginalLines] = useState([]);
    const [editingExistingRecipe, setEditingExistingRecipe] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showMessage = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    const loadData = useCallback(
        async (showLoading = true) => {
            if (showLoading) {
                setLoading(true);
            }

            try {
                const [productResult, inventoryResult, recipeResult] = await Promise.all([
                    productAPI.getAll({
                        force: true,
                        fallback: [],
                    }),
                    inventoryAPI.getAll({
                        force: true,
                        fallback: [],
                    }),
                    itemRecipeAPI.getAll({
                        force: true,
                        fallback: [],
                    }),
                ]);

                setProducts(Array.isArray(productResult) ? productResult : []);

                setInventory(Array.isArray(inventoryResult) ? inventoryResult : []);

                setRecipeLines(Array.isArray(recipeResult) ? recipeResult : []);

                return true;
            } catch (error) {
                console.error(error);

                showMessage(
                    error.message || t('item_recipes_fetch_error', 'Could not load menu recipes.'),
                    'error'
                );

                return false;
            } finally {
                if (showLoading) {
                    setLoading(false);
                }
            }
        },
        [t]
    );

    useEffect(() => {
        loadData();
    }, [loadData]);

    const linesByItem = useMemo(() => {
        const grouped = new Map();

        recipeLines.forEach((line) => {
            const key = String(typeof line.item === 'object' ? line.item?.id : line.item);

            if (!grouped.has(key)) {
                grouped.set(key, []);
            }

            grouped.get(key).push(line);
        });

        return grouped;
    }, [recipeLines]);

    const openCreate = () => {
        setSelectedItemId('');
        setIngredients([emptyIngredient()]);
        setOriginalLines([]);
        setEditingExistingRecipe(false);
        setDialogOpen(true);
    };

    const openEdit = async (product) => {
        try {
            const lines = await itemRecipeAPI.getForItem(product.id, {
                force: true,
                fallback: [],
            });

            const safeLines = Array.isArray(lines) ? lines : [];

            setSelectedItemId(String(product.id));
            setOriginalLines(safeLines);
            setEditingExistingRecipe(true);

            setIngredients(
                safeLines.length
                    ? safeLines.map((line) => ({
                          id: line.id,
                          ingredient: String(
                              typeof line.ingredient === 'object'
                                  ? line.ingredient?.id
                                  : line.ingredient
                          ),
                          quantity: String(line.quantity),
                      }))
                    : [emptyIngredient()]
            );

            setDialogOpen(true);
        } catch (error) {
            console.error(error);

            showMessage(
                error.message || t('item_recipes_fetch_error', 'Could not load this recipe.'),
                'error'
            );
        }
    };

    const closeDialog = () => {
        if (saving) return;

        setDialogOpen(false);
        setSelectedItemId('');
        setIngredients([]);
        setOriginalLines([]);
        setEditingExistingRecipe(false);
    };

    const updateIngredient = (index, field, value) => {
        setIngredients((current) =>
            current.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
        );
    };

    const addIngredient = () => {
        setIngredients((current) => [...current, emptyIngredient()]);
    };

    const removeIngredient = (index) => {
        setIngredients((current) => current.filter((_, rowIndex) => rowIndex !== index));
    };

    const validate = () => {
        if (!selectedItemId) {
            showMessage(t('item_recipe_select_item', 'Select a menu item first.'), 'error');

            return false;
        }

        if (!ingredients.length) {
            showMessage(t('item_recipe_add_ingredient', 'Add at least one ingredient.'), 'error');

            return false;
        }

        const hasInvalidIngredient = ingredients.some(
            (row) =>
                !row.ingredient ||
                row.quantity === '' ||
                !Number.isFinite(Number(row.quantity)) ||
                Number(row.quantity) <= 0
        );

        if (hasInvalidIngredient) {
            showMessage(
                t(
                    'item_recipe_invalid_ingredient',
                    'Choose an ingredient and enter a quantity greater than zero.'
                ),
                'error'
            );

            return false;
        }

        const ingredientIds = ingredients.map((row) => String(row.ingredient));

        if (new Set(ingredientIds).size !== ingredientIds.length) {
            showMessage(
                t(
                    'item_recipe_duplicate_ingredient',
                    'An ingredient can only appear once in a recipe.'
                ),
                'error'
            );

            return false;
        }

        return true;
    };

    const saveRecipe = async () => {
        if (!validate()) return;

        setSaving(true);

        try {
            const itemId = Number(selectedItemId);

            const desiredExistingIds = new Set(
                ingredients.filter((row) => row.id).map((row) => Number(row.id))
            );

            const saveRequests = ingredients.map((row) => {
                const payload = {
                    item: itemId,
                    ingredient: Number(row.ingredient),
                    quantity: Number(row.quantity),
                };

                if (row.id) {
                    return itemRecipeAPI.update(row.id, payload);
                }

                return itemRecipeAPI.create(payload);
            });

            await Promise.all(saveRequests);

            const deleteRequests = originalLines
                .filter((oldLine) => !desiredExistingIds.has(Number(oldLine.id)))
                .map((oldLine) => itemRecipeAPI.remove(oldLine.id));

            await Promise.all(deleteRequests);

            const refreshed = await loadData(false);

            if (!refreshed) {
                showMessage(t('item_recipe_save_success', 'Menu item recipe saved.'), 'success');
            } else {
                showMessage(t('item_recipe_save_success', 'Menu item recipe saved.'), 'success');
            }

            setDialogOpen(false);
            setSelectedItemId('');
            setIngredients([]);
            setOriginalLines([]);
            setEditingExistingRecipe(false);
        } catch (error) {
            console.error(error);

            showMessage(
                error.message || t('item_recipe_save_error', 'Could not save the recipe.'),
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const getIngredientUnit = (id) =>
        inventory.find((entry) => String(entry.id) === String(id))?.unit || '';

    const renderProductCard = (product) => {
        const lines = linesByItem.get(String(product.id)) || [];

        return (
            <Paper className="formulation_card" key={product.id}>
                <div className="item-recipe-card-content">
                    <div className="formulation_card_header">
                        <div>
                            <h2>{getProductName(product)}</h2>

                            <span className="formulation_output">
                                {t('ingredients_count', 'Ingredients')}:
                                <strong>{lines.length}</strong>
                            </span>
                        </div>

                        <div className="formulation_card_actions">
                            <IconButton
                                onClick={() => openEdit(product)}
                                aria-label={t('edit_item_recipe', 'Edit recipe')}
                            >
                                <EditIcon />
                            </IconButton>
                        </div>
                    </div>

                    <div className="formulation_ingredients">
                        <div className="formulation_ingredients_title">
                            <RestaurantMenuIcon />

                            <span>{t('ingredients')}</span>
                        </div>

                        {lines.length > 0 ? (
                            <div className="formulation_ingredient_list">
                                {lines.slice(0, 4).map((line) => {
                                    const ingredientId =
                                        typeof line.ingredient === 'object'
                                            ? line.ingredient?.id
                                            : line.ingredient;

                                    const stockItem = inventory.find(
                                        (entry) => String(entry.id) === String(ingredientId)
                                    );

                                    return (
                                        <div className="formulation_ingredient" key={line.id}>
                                            <span>
                                                {line.ingredient_name ||
                                                    stockItem?.name ||
                                                    `Ingredient #${ingredientId}`}
                                            </span>

                                            <strong>
                                                {line.quantity}{' '}
                                                {line.ingredient_unit || stockItem?.unit || ''}
                                            </strong>
                                        </div>
                                    );
                                })}

                                {lines.length > 4 && (
                                    <div className="formulation_no_ingredients">
                                        +{lines.length - 4} {t('more', 'more')}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="formulation_no_ingredients">
                                {t('item_recipe_not_defined', 'No recipe defined yet.')}
                            </div>
                        )}
                    </div>
                </div>

                <Button
                    className="adding-recipe-button"
                    fullWidth
                    variant="outlined"
                    onClick={() => openEdit(product)}
                >
                    {lines.length > 0
                        ? t('edit_item_recipe', 'Edit recipe')
                        : t('add_item_recipe', 'Add recipe')}
                </Button>
            </Paper>
        );
    };

    return (
        <section className="item-recipes-section">
            <div className="item-recipes-header">
                <div>
                    <h2>{t('menu_item_recipes_title', 'Menu item recipes')}</h2>

                    <p>
                        {t(
                            'menu_item_recipes_description',
                            'Define the inventory ingredients required for each menu item.'
                        )}
                    </p>
                </div>
            </div>

            {loading && (
                <div className="item-recipes-loading">
                    <CircularProgress size={28} />
                </div>
            )}

            {!loading && products.length === 0 && (
                <Paper className="item-recipes-empty">
                    <RestaurantMenuIcon />

                    <h3>{t('item_recipes_no_products', 'No menu items found')}</h3>

                    <p>
                        {t(
                            'item_recipes_no_products_description',
                            'Create menu items before defining their recipes.'
                        )}
                    </p>
                </Paper>
            )}

            {!loading && products.length > 0 && (
                <div className="item-recipes-grid">{products.map(renderProductCard)}</div>
            )}

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>{t('menu_item_recipe_dialog_title', 'Menu item recipe')}</DialogTitle>

                <DialogContent>
                    <div className="item-recipe-form">
                        <FormControl fullWidth>
                            <InputLabel id="recipe-menu-item-label">
                                {t('menu_item', 'Menu item')}
                            </InputLabel>

                            <Select
                                labelId="recipe-menu-item-label"
                                value={selectedItemId}
                                label={t('menu_item', 'Menu item')}
                                onChange={(event) => {
                                    setSelectedItemId(event.target.value);
                                    setIngredients([emptyIngredient()]);
                                    setOriginalLines([]);
                                }}
                                disabled={saving || editingExistingRecipe}
                            >
                                {products.map((product) => (
                                    <MenuItem key={product.id} value={String(product.id)}>
                                        {getProductName(product)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <div className="item-recipe-form-heading">
                            <h3>{t('formulation_ingredients', 'Ingredients')}</h3>

                            <span>
                                {t('quantity_per_menu_item', 'Quantity required for one menu item')}
                            </span>
                        </div>

                        {ingredients.map((row, index) => (
                            <div className="item-recipe-edit-row" key={row.id || `new-${index}`}>
                                <FormControl fullWidth>
                                    <InputLabel id={`recipe-ingredient-label-${index}`}>
                                        {t('inventory_ingredient', 'Inventory ingredient')}
                                    </InputLabel>

                                    <Select
                                        labelId={`recipe-ingredient-label-${index}`}
                                        value={row.ingredient}
                                        label={t('inventory_ingredient', 'Inventory ingredient')}
                                        onChange={(event) =>
                                            updateIngredient(
                                                index,
                                                'ingredient',
                                                event.target.value
                                            )
                                        }
                                        disabled={saving}
                                    >
                                        {inventory.map((entry) => (
                                            <MenuItem key={entry.id} value={String(entry.id)}>
                                                {entry.name} ({entry.unit})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    type="number"
                                    label={`${t('quantity', 'Quantity')}${
                                        getIngredientUnit(row.ingredient)
                                            ? ` (${getIngredientUnit(row.ingredient)})`
                                            : ''
                                    }`}
                                    value={row.quantity}
                                    onChange={(event) =>
                                        updateIngredient(index, 'quantity', event.target.value)
                                    }
                                    inputProps={{
                                        min: 0,
                                        step: '0.0001',
                                    }}
                                    disabled={saving}
                                />

                                <IconButton
                                    color="error"
                                    onClick={() => removeIngredient(index)}
                                    disabled={saving}
                                    aria-label={t('remove_ingredient', 'Remove ingredient')}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </div>
                        ))}

                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addIngredient}
                            disabled={saving}
                        >
                            {t('add_ingredient', 'Add ingredient')}
                        </Button>
                    </div>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>
                        {t('cancel', 'Cancel')}
                    </Button>

                    <Button variant="contained" onClick={saveRecipe} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : t('save', 'Save recipe')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() =>
                    setSnackbar((current) => ({
                        ...current,
                        open: false,
                    }))
                }
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() =>
                        setSnackbar((current) => ({
                            ...current,
                            open: false,
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </section>
    );
}

export default ItemRecipes;
